// A visitor who fills in a request before having an account is sent to sign up,
// and the request waits in this browser's localStorage for an hour
// (UploadPrescription.tsx saves it and restores it once someone is signed in).
// Only the upload page restores it, so every sign-in path that would otherwise
// land on a dashboard sends the visitor back there while a request is waiting.

export const PENDING_PRESCRIPTION_MAX_AGE_MS = 60 * 60 * 1000;

export interface PendingPrescriptionSummary {
  medicationName: string;
  dosage: string;
  /** The state chosen on the review step, if any (prefills the sign-up form). */
  state?: string;
}

function readFresh(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('pendingPrescription');
    const savedAt = Number(window.localStorage.getItem('pendingPrescriptionSavedAt') || '0');
    if (!raw || !savedAt || Date.now() - savedAt >= PENDING_PRESCRIPTION_MAX_AGE_MS) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function hasFreshPendingPrescription(): boolean {
  return readFresh() !== null;
}

/** Medication and strength of the waiting request, for a reminder on screen. */
export function pendingPrescriptionSummary(): PendingPrescriptionSummary | null {
  const data = readFresh();
  if (!data) return null;
  const medicationName = typeof data.medication_name === 'string' ? data.medication_name.trim().slice(0, 80) : '';
  const dosage = typeof data.dosage === 'string' ? data.dosage.trim().slice(0, 60) : '';
  const state = typeof data.patient_state === 'string' ? data.patient_state.trim().slice(0, 40) : '';
  return medicationName ? { medicationName, dosage, ...(state ? { state } : {}) } : null;
}

/** Where a patient should land after signing in. */
export function patientHomeAfterSignIn(): '/upload-prescription' | '/dashboard' {
  return hasFreshPendingPrescription() ? '/upload-prescription' : '/dashboard';
}
